import axios from 'axios';
import { batchGenerateAndSaveItineraries, smartBatchProcess } from './batchClusteringService';
import { completeV2WorkflowWithRetry } from './v2ApiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';

// Feature flag to enable v2 API
const USE_V2_API = process.env.REACT_APP_USE_V2_API === 'true' || true; // Default to v2

/**
 * Filter stops based on criteria
 * @param {Object} filters - Filter criteria (regions, markets, states, cities, center, radius)
 * @param {string} token - JWT authentication token
 * @returns {Promise<{stops: Array, total_count: number}>}
 */
export const filterStops = async (filters, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v2/stops/filter`,
    {
      filters,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  // Adapt the V2 response to the legacy format expected by V1 functions
  return {
    stops: response.data.stops,
    total_count: response.data.total_stops,
  };
};

/**
 * Cluster a set of stops geographically
 * @param {Array} stops - Array of stops from filter-stops endpoint
 * @param {Object} clusteringParams - Optional clustering parameters (eps, min_samples, use_v3, stops_per_day)
 * @param {string} startDate - Trip start date (YYYY-MM-DD format)
 * @param {string} endDate - Trip end date (YYYY-MM-DD format)
 * @returns {Promise<{clusters: Array, total_clusters: number}>}
 */
export const clusterStops = async (stops, clusteringParams = {}, startDate = null, endDate = null) => {
  const payload = {
    stops,
    clustering_params: clusteringParams
  };
  
  // Add dates if provided (required for proper cluster sizing)
  if (startDate) payload.startDate = startDate;
  if (endDate) payload.endDate = endDate;
  
  const response = await axios.post(`${API_BASE_URL}/cluster-stops`, payload);
  return response.data;
};

/**
 * Generate AI-optimized itinerary for a cluster
 * @param {Object} clusterData - Complete cluster object from cluster-stops
 * @param {string} startDate - Optional start date (YYYY-MM-DD format)
 * @param {string} endDate - Optional end date (YYYY-MM-DD format)
 * @returns {Promise<{itinerary: Object, ai_generated: boolean}>}
 */
export const generateItinerary = async (clusterData, startDate, endDate) => {
  const payload = {
    cluster_data: clusterData
  };
  
  if (startDate) payload.start_date = startDate;
  if (endDate) payload.end_date = endDate;
  
  const response = await axios.post(`${API_BASE_URL}/generate-itinerary`, payload);
  return response.data;
};

/**
 * Save itinerary to database (requires authentication)
 * @param {Object} clusterData - Complete cluster object
 * @param {Object} itineraryData - Itinerary from generate-itinerary
 * @param {Object} filterParams - Original filter params
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - Frontend-compatible cluster object
 */
export const saveItinerary = async (clusterData, itineraryData, filterParams, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/save-itinerary`,
    {
      cluster_data: clusterData,
      itinerary_data: itineraryData,
      filter_params: filterParams
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  return response.data;
};

/**
 * Complete clustering flow - replaces old /cluster endpoint
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClusters = async (filters, clusteringParams, token, onProgress) => {
  const errors = [];
  const results = {
    stops: [],
    clusters: [],
    finalClusters: []
  };
  
  try {
    // Step 1: Filter stops
    if (onProgress) onProgress({ step: 'filter', message: 'Finding customers...' });
    let filterData;
    try {
      filterData = await filterStops(filters, token);
      results.stops = filterData.stops;
    } catch (error) {
      const errorMsg = `Failed to filter stops: ${error.response?.data?.detail || error.message}`;
      errors.push(errorMsg);
      throw new Error(errorMsg);
    }
    
    if (!filterData.stops || filterData.stops.length === 0) {
      const msg = 'No customers found matching your filters';
      errors.push(msg);
      return [];
    }
    
    // Step 2: Cluster the stops
    if (onProgress) onProgress({ step: 'cluster', message: 'Grouping customers...' });
    let clusterData;
    try {
      clusterData = await clusterStops(filterData.stops, clusteringParams, filters.startDate, filters.endDate);
      results.clusters = clusterData.clusters;
    } catch (error) {
      const errorMsg = `Failed to cluster stops: ${error.response?.data?.detail || error.message}`;
      errors.push(errorMsg);
      throw new Error(errorMsg);
    }
    
    if (!clusterData.clusters || clusterData.clusters.length === 0) {
      const msg = 'No clusters could be formed with the current parameters';
      errors.push(msg);
      return [];
    }
    
    // Step 3 & 4: Generate itineraries and save for each cluster
    const finalClusters = [];
    const clusterErrors = [];
    
    for (let i = 0; i < clusterData.clusters.length; i++) {
      const cluster = clusterData.clusters[i];
      
      if (onProgress) {
        onProgress({ 
          step: 'itinerary', 
          message: `Generating itinerary ${i + 1} of ${clusterData.clusters.length}...` 
        });
      }
      
      try {
        // Generate AI itinerary
        let itineraryData;
        try {
          const itineraryResponse = await generateItinerary(
            cluster, 
            filters.startDate, 
            filters.endDate
          );
          itineraryData = itineraryResponse.itinerary;
        } catch (error) {
          console.warn(`Failed to generate AI itinerary for cluster ${cluster.cluster_id}:`, error);
          // Create a basic itinerary if AI generation fails
          itineraryData = {
            name: `Cluster ${cluster.cluster_id} Itinerary`,
            description: `Route for ${cluster.stops.length} stops`,
            schedule: [{
              day: 'Day 1',
              activities: cluster.stops.map((stop, idx) => 
                `Visit ${idx + 1}: ${stop.customer_name} at ${stop.address}, ${stop.city}, ${stop.state}`
              )
            }],
            summary: `Visit ${cluster.stops.length} customers in this cluster.`,
            cluster_id: cluster.cluster_id,
            generated_at: new Date().toISOString(),
            ai_generated: false
          };
        }
        
        // Save to database
        if (onProgress) {
          onProgress({ 
            step: 'save', 
            message: `Saving itinerary ${i + 1} of ${clusterData.clusters.length}...` 
          });
        }
        
        try {
          const savedCluster = await saveItinerary(
            cluster,
            itineraryData,
            { filters },
            token
          );
          finalClusters.push(savedCluster);
        } catch (error) {
          const errorMsg = `Failed to save cluster ${cluster.cluster_id}: ${error.response?.data?.detail || error.message}`;
          clusterErrors.push(errorMsg);
          console.error(errorMsg, error);
        }
        
      } catch (error) {
        const errorMsg = `Failed to process cluster ${cluster.cluster_id}: ${error.message}`;
        clusterErrors.push(errorMsg);
        console.error(errorMsg, error);
      }
    }
    
    // If some clusters were processed successfully
    if (finalClusters.length > 0) {
      if (clusterErrors.length > 0) {
        console.warn(`Processed ${finalClusters.length} of ${clusterData.clusters.length} clusters. Errors: `, clusterErrors);
      }
      return finalClusters;
    }
    
    // If no clusters were saved successfully
    if (clusterErrors.length > 0) {
      throw new Error(`Failed to save any clusters: ${clusterErrors.join('; ')}`);
    }
    
    return [];
    
  } catch (error) {
    console.error('Clustering process failed:', error);
    // Re-throw with more context
    if (error.message.includes('Failed to')) {
      throw error;
    }
    throw new Error(`Clustering failed: ${error.message}`);
  }
};

/**
 * Create clusters with retry logic for different min_samples values
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Initial clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClustersWithRetry = async (filters, clusteringParams, token, onProgress) => {
  const originalMinSamples = clusteringParams.min_samples || 12;
  const fallbackValues = [8, 5, 3, 2, 1];
  
  // Try with original min_samples first
  try {
    console.log(`Attempting clustering with min_samples: ${originalMinSamples}`);
    const result = await createClusters(filters, clusteringParams, token, onProgress);
    
    if (result.length > 0) {
      return result;
    }
  } catch (error) {
    console.error(`Clustering failed with min_samples ${originalMinSamples}:`, error);
  }
  
  // Try with progressively smaller min_samples values
  for (const fallbackMinSamples of fallbackValues) {
    if (fallbackMinSamples >= originalMinSamples) continue;
    
    try {
      console.log(`Retrying clustering with reduced min_samples: ${fallbackMinSamples}`);
      const fallbackParams = { ...clusteringParams, min_samples: fallbackMinSamples };
      const result = await createClusters(filters, fallbackParams, token, onProgress);
      
      if (result.length > 0) {
        console.log(`Successfully generated schedules with min_samples: ${fallbackMinSamples}`);
        return result;
      }
    } catch (error) {
      console.error(`Clustering failed with min_samples ${fallbackMinSamples}:`, error);
    }
  }
  
  throw new Error('Failed to generate clusters with all attempted min_samples values');
};

/**
 * Optimized clustering flow with batch processing
 * Uses batch endpoints for better performance when processing multiple clusters
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @param {boolean} useBatchProcessing - Whether to use batch processing (default: true)
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClustersOptimized = async (filters, clusteringParams, token, onProgress, useBatchProcessing = true) => {
  const errors = [];
  const results = {
    stops: [],
    clusters: [],
    finalClusters: []
  };
  
  try {
    // Step 1: Filter stops
    if (onProgress) onProgress({ step: 'filter', message: 'Finding customers...' });
    let filterData;
    try {
      filterData = await filterStops(filters, token);
      results.stops = filterData.stops;
    } catch (error) {
      const errorMsg = `Failed to filter stops: ${error.response?.data?.detail || error.message}`;
      errors.push(errorMsg);
      throw new Error(errorMsg);
    }
    
    if (!filterData.stops || filterData.stops.length === 0) {
      const msg = 'No customers found matching your filters';
      errors.push(msg);
      return [];
    }
    
    // Step 2: Cluster the stops
    if (onProgress) onProgress({ step: 'cluster', message: 'Grouping customers...' });
    let clusterData;
    try {
      clusterData = await clusterStops(filterData.stops, clusteringParams, filters.startDate, filters.endDate);
      results.clusters = clusterData.clusters;
    } catch (error) {
      const errorMsg = `Failed to cluster stops: ${error.response?.data?.detail || error.message}`;
      errors.push(errorMsg);
      throw new Error(errorMsg);
    }
    
    if (!clusterData.clusters || clusterData.clusters.length === 0) {
      const msg = 'No clusters could be formed with the current parameters';
      errors.push(msg);
      return [];
    }
    
    // Step 3 & 4: Batch process or sequential process based on cluster count and preference
    const clusterCount = clusterData.clusters.length;
    
    if (useBatchProcessing && clusterCount > 1) {
      // Use batch processing for multiple clusters
      if (onProgress) {
        onProgress({ 
          step: 'batch', 
          message: `Processing ${clusterCount} clusters in parallel...` 
        });
      }
      
      try {
        const batchResult = await smartBatchProcess(
          clusterData.clusters,
          filters.startDate,
          filters.endDate,
          { filters },
          token,
          onProgress
        );
        
        if (batchResult.successful) {
          return batchResult.successful;
        }
        
        // If batch result is array format
        if (Array.isArray(batchResult)) {
          return batchResult;
        }
        
        return [];
        
      } catch (error) {
        console.error('Batch processing failed, falling back to sequential:', error);
        // Fall through to sequential processing
      }
    }
    
    // Sequential processing (fallback or when batch is disabled)
    const finalClusters = [];
    const clusterErrors = [];
    
    for (let i = 0; i < clusterData.clusters.length; i++) {
      const cluster = clusterData.clusters[i];
      
      if (onProgress) {
        onProgress({ 
          step: 'itinerary', 
          message: `Generating itinerary ${i + 1} of ${clusterData.clusters.length}...` 
        });
      }
      
      try {
        // Generate AI itinerary
        let itineraryData;
        try {
          const itineraryResponse = await generateItinerary(
            cluster, 
            filters.startDate, 
            filters.endDate
          );
          itineraryData = itineraryResponse.itinerary;
        } catch (error) {
          console.warn(`Failed to generate AI itinerary for cluster ${cluster.cluster_id}:`, error);
          // Create a basic itinerary if AI generation fails
          itineraryData = {
            name: `Cluster ${cluster.cluster_id} Itinerary`,
            description: `Route for ${cluster.stops.length} stops`,
            schedule: [{
              day: 'Day 1',
              activities: cluster.stops.map((stop, idx) => 
                `Visit ${idx + 1}: ${stop.customer_name} at ${stop.address}, ${stop.city}, ${stop.state}`
              )
            }],
            summary: `Visit ${cluster.stops.length} customers in this cluster.`,
            cluster_id: cluster.cluster_id,
            generated_at: new Date().toISOString(),
            ai_generated: false
          };
        }
        
        // Save to database
        if (onProgress) {
          onProgress({ 
            step: 'save', 
            message: `Saving itinerary ${i + 1} of ${clusterData.clusters.length}...` 
          });
        }
        
        try {
          const savedCluster = await saveItinerary(
            cluster,
            itineraryData,
            { filters },
            token
          );
          finalClusters.push(savedCluster);
        } catch (error) {
          const errorMsg = `Failed to save cluster ${cluster.cluster_id}: ${error.response?.data?.detail || error.message}`;
          clusterErrors.push(errorMsg);
          console.error(errorMsg, error);
        }
        
      } catch (error) {
        const errorMsg = `Failed to process cluster ${cluster.cluster_id}: ${error.message}`;
        clusterErrors.push(errorMsg);
        console.error(errorMsg, error);
      }
    }
    
    // If some clusters were processed successfully
    if (finalClusters.length > 0) {
      if (clusterErrors.length > 0) {
        console.warn(`Processed ${finalClusters.length} of ${clusterData.clusters.length} clusters. Errors: `, clusterErrors);
      }
      return finalClusters;
    }
    
    // If no clusters were saved successfully
    if (clusterErrors.length > 0) {
      throw new Error(`Failed to save any clusters: ${clusterErrors.join('; ')}`);
    }
    
    return [];
    
  } catch (error) {
    console.error('Clustering process failed:', error);
    // Re-throw with more context
    if (error.message.includes('Failed to')) {
      throw error;
    }
    throw new Error(`Clustering failed: ${error.message}`);
  }
};

/**
 * Create clusters with retry logic using optimized batch processing
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Initial clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @param {boolean} useBatchProcessing - Whether to use batch processing (default: true)
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClustersWithRetryOptimized = async (filters, clusteringParams, token, onProgress, useBatchProcessing = true) => {
  const originalMinSamples = clusteringParams.min_samples || 12;
  const fallbackValues = [8, 5, 3, 2, 1];
  
  // Try with original min_samples first
  try {
    console.log(`Attempting optimized clustering with min_samples: ${originalMinSamples}`);
    const result = await createClustersOptimized(filters, clusteringParams, token, onProgress, useBatchProcessing);
    
    if (result.length > 0) {
      return result;
    }
  } catch (error) {
    console.error(`Optimized clustering failed with min_samples ${originalMinSamples}:`, error);
  }
  
  // Try with progressively smaller min_samples values
  for (const fallbackMinSamples of fallbackValues) {
    if (fallbackMinSamples >= originalMinSamples) continue;
    
    try {
      console.log(`Retrying optimized clustering with reduced min_samples: ${fallbackMinSamples}`);
      const fallbackParams = { ...clusteringParams, min_samples: fallbackMinSamples };
      const result = await createClustersOptimized(filters, fallbackParams, token, onProgress, useBatchProcessing);
      
      if (result.length > 0) {
        console.log(`Successfully generated schedules with min_samples: ${fallbackMinSamples}`);
        return result;
      }
    } catch (error) {
      console.error(`Optimized clustering failed with min_samples ${fallbackMinSamples}:`, error);
    }
  }
  
  throw new Error('Failed to generate clusters with all attempted min_samples values');
};

/**
 * Universal clustering function that automatically uses v2 API when available
 * This is the recommended function to use for all clustering operations
 * @param {Object} filters - Filter criteria
 * @param {Object} clusteringParams - Clustering parameters
 * @param {string} token - JWT authentication token
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} - Array of saved cluster objects
 */
export const createClustersUniversal = async (filters, clusteringParams, token, onProgress) => {
  // If v2 API is enabled, use it
  if (USE_V2_API) {
    console.log("Using v2 API for clustering");
    
    // Convert v1 params to v2 format
    const v2ClusteringParams = {
      start_date: filters.startDate,
      end_date: filters.endDate,
      stops_per_day: clusteringParams.stops_per_day || 5
    };
    
    const tripDetails = {
      start_date: filters.startDate,
      end_date: filters.endDate,
      start_time: "8:00 AM",
      end_time: "6:00 PM"
    };
    
    // Convert v1 filters to v2 format
    const v2Filters = {
      market: filters.markets,
      sales_region: filters.regions,
      states: filters.states,
      opportunity_type: filters.priority_buckets || ["Quote", "Order", "Rental"],
      min_revenue: filters.min_revenue,
      max_revenue: filters.max_revenue
    };
    
    // Remove undefined values
    Object.keys(v2Filters).forEach(key => {
      if (v2Filters[key] === undefined || (Array.isArray(v2Filters[key]) && v2Filters[key].length === 0)) {
        delete v2Filters[key];
      }
    });
    
    try {
      return await completeV2WorkflowWithRetry(
        v2Filters,
        v2ClusteringParams,
        tripDetails,
        token,
        onProgress
      );
    } catch (error) {
      console.error("V2 API failed, falling back to v1:", error);
      // Fall through to v1 API
    }
  }
  
  // Use v1 API (existing implementation)
  console.log("Using v1 API for clustering");
  return await createClustersWithRetryOptimized(
    filters,
    clusteringParams,
    token,
    onProgress,
    true // Enable batch processing
  );
};
